/**
 * Slice 11 — First property: choice → Hospitable import (preview) · Manual
 * Matches Claude Design FP2–FP7 exactly.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useCreateProperty, useProperties, useUpdateProperty } from "@/hooks/useProperties";
import { cn } from "@/lib/utils";

type Step = "choice" | "connect" | "units" | "preview" | "importing" | "done" | "manual";

type HospitableUnit = { id: string; name: string; address: string; picture_url?: string | null };

type ImportPreview = {
  id: string;
  name: string;
  address: string;
  street: string | null;
  city: string | null;
  state_province: string | null;
  postal_code: string | null;
  country: string | null;
  formatted_address: string | null;
  latitude: number | null;
  longitude: number | null;
  beds: number | null;
  baths: number | null;
  bedrooms: number | null;
  rooms_count: number | null;
  room_labels: string[];
  check_in: string | null;
  check_out: string | null;
  summary: string | null;
  ical_url: string | null;
  image_urls: string[];
  cover_url: string | null;
};

async function invokeHospitable(body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke("hospitable-proxy", { body });
  if (error) throw new Error(error.message || "Hospitable request failed");
  if (data?.error) throw new Error(String(data.error));
  return data as Record<string, unknown>;
}

/** Convert "4:00 PM" / "15:00" → "15:00:00" for Postgres time columns. */
function toDbTime(raw: string | null | undefined): string | null {
  if (!raw?.trim()) return null;
  const s = raw.trim();
  const ampm = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampm) {
    let h = Number(ampm[1]);
    const m = ampm[2];
    const ap = ampm[3].toUpperCase();
    if (ap === "PM" && h < 12) h += 12;
    if (ap === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${m}:00`;
  }
  const h24 = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (h24) {
    return `${String(Number(h24[1])).padStart(2, "0")}:${h24[2]}:${h24[3] || "00"}`;
  }
  return null;
}

function shortId(id: string) {
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

function dash(v: string | number | null | undefined) {
  if (v == null || v === "") return "—";
  return String(v);
}

type Props = {
  onClose: () => void;
};

export default function AddPropertySheet({ onClose }: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: properties = [] } = useProperties();
  const createProperty = useCreateProperty();
  const updateProperty = useUpdateProperty();

  const [step, setStep] = useState<Step>("choice");
  const [patInput, setPatInput] = useState("");
  const [connected, setConnected] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [units, setUnits] = useState<HospitableUnit[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importStatus, setImportStatus] = useState("Address");
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [doneChips, setDoneChips] = useState<string[]>([]);

  const [manualName, setManualName] = useState("");
  const [manualAddress, setManualAddress] = useState("");
  const [manualUnit, setManualUnit] = useState("");

  const linkedHospitableIds = useMemo(() => {
    const set = new Set<string>();
    for (const p of properties as Array<{ hospitable_property_id?: string | null }>) {
      const hid = String(p.hospitable_property_id || "").trim();
      if (hid) set.add(hid);
    }
    return set;
  }, [properties]);

  const sheetTitle: Record<Step, string> = {
    choice: "Add property",
    connect: "Hospitable",
    units: "Units",
    preview: "Import",
    importing: "Import",
    done: "Import",
    manual: "Manual",
  };

  const canBack = ["connect", "units", "preview", "manual"].includes(step);

  const refreshStatus = useCallback(async () => {
    if (!user) return;
    setStatusLoading(true);
    try {
      const data = await invokeHospitable({ action: "status" });
      setConnected(Boolean(data.connected));
    } catch {
      setConnected(false);
    } finally {
      setStatusLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  const loadUnits = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await invokeHospitable({ action: "list_properties" });
      const list = Array.isArray(data.properties) ? (data.properties as HospitableUnit[]) : [];
      setUnits(list);
      setSelectedId((prev) => {
        if (prev && list.some((u) => u.id === prev && !linkedHospitableIds.has(u.id))) return prev;
        const firstFree = list.find((u) => !linkedHospitableIds.has(u.id));
        return firstFree?.id || "";
      });
    } catch (err) {
      toast({
        title: "Could not list properties",
        description: err instanceof Error ? err.message : "Failed",
        variant: "destructive",
      });
    } finally {
      setLoadingList(false);
    }
  }, [linkedHospitableIds]);

  const goBack = () => {
    if (step === "connect" || step === "manual") setStep("choice");
    else if (step === "units") setStep(connected ? "choice" : "connect");
    else if (step === "preview") setStep("units");
  };

  const goHospitable = async () => {
    if (statusLoading) return;
    if (connected) {
      setStep("units");
      void loadUnits();
    } else {
      setStep("connect");
    }
  };

  const savePat = async () => {
    setBusy(true);
    try {
      const data = await invokeHospitable({ action: "save_pat", pat: patInput });
      setConnected(Boolean(data.connected));
      setPatInput("");
      toast({ title: "Connected" });
      setStep("units");
      await loadUnits();
    } catch (err) {
      toast({
        title: "Could not connect",
        description: err instanceof Error ? err.message : "Failed",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const goPreview = async () => {
    if (!selectedId) return;
    setPreviewLoading(true);
    setStep("preview");
    try {
      const data = await invokeHospitable({
        action: "get_property",
        hospitable_property_id: selectedId,
      });
      const p = data.property as ImportPreview;
      setPreview(p);
    } catch (err) {
      toast({
        title: "Could not load unit",
        description: err instanceof Error ? err.message : "Failed",
        variant: "destructive",
      });
      setStep("units");
    } finally {
      setPreviewLoading(false);
    }
  };

  const runImport = async () => {
    if (!preview) return;
    if (linkedHospitableIds.has(preview.id)) {
      toast({ title: "Already linked", description: "That unit is already in the hub.", variant: "destructive" });
      return;
    }
    setStep("importing");
    setProgress(8);
    setImportStatus("Address");
    setBusy(true);
    try {
      setProgress(22);
      const created = await createProperty.mutateAsync({
        name: preview.name,
        address: preview.street || preview.address || undefined,
        city: preview.city || undefined,
        state_province: preview.state_province || undefined,
        postal_code: preview.postal_code || undefined,
        country: preview.country || undefined,
        formatted_address: preview.formatted_address || preview.address || undefined,
        latitude: preview.latitude,
        longitude: preview.longitude,
        hospitable_property_id: preview.id,
      });
      setCreatedId(created.id);
      setProgress(40);
      setImportStatus("Photos");

      let photoUrls = preview.image_urls || [];
      let cover = preview.cover_url || photoUrls[0] || null;
      if (photoUrls.length > 0) {
        try {
          const mirrored = await invokeHospitable({
            action: "mirror_images",
            property_id: created.id,
            image_urls: photoUrls,
          });
          const urls = Array.isArray(mirrored.image_urls)
            ? (mirrored.image_urls as string[]).filter(Boolean)
            : [];
          if (urls.length) {
            photoUrls = urls;
            cover = urls[0];
          }
        } catch {
          // keep remote URLs if mirror fails
        }
      }

      setProgress(62);
      setImportStatus("Listing");

      await updateProperty.mutateAsync({
        propertyId: created.id,
        property_photo_url: cover,
        property_photo_urls: photoUrls.length ? photoUrls : null,
        notes: preview.summary || undefined,
        default_checkin_time: toDbTime(preview.check_in),
        default_checkout_time: toDbTime(preview.check_out),
      });

      if (preview.ical_url) {
        await supabase
          .from("properties")
          .update({ airbnb_ical_url: preview.ical_url })
          .eq("id", created.id);
      }

      setProgress(78);
      setImportStatus("Calendar");
      // Full Hospitable pull: full-res photos + future reservations + turnover tasks
      try {
        const synced = await invokeHospitable({
          action: "sync_from_hospitable",
          property_id: created.id,
          hospitable_property_id: preview.id,
        });
        const urls = Array.isArray(synced.image_urls) ? (synced.image_urls as string[]) : [];
        if (urls.length) {
          photoUrls = urls;
          cover = (synced.cover_url as string) || urls[0];
        }
      } catch {
        // non-fatal — property already created
      }
      setProgress(100);

      const chips: string[] = [];
      if (photoUrls.length) chips.push(`${photoUrls.length} photo${photoUrls.length === 1 ? "" : "s"}`);
      if (labels.length) chips.push(`${labels.length} room${labels.length === 1 ? "" : "s"}`);
      if (preview.check_in || preview.check_out) chips.push("Times");
      if (preview.ical_url) chips.push("Calendar");
      chips.push("Linked");
      setDoneChips(chips);
      setStep("done");
    } catch (err) {
      toast({
        title: "Import failed",
        description: err instanceof Error ? err.message : "Failed",
        variant: "destructive",
      });
      setStep("preview");
    } finally {
      setBusy(false);
    }
  };

  const saveManual = async () => {
    const name = manualName.trim();
    if (!name) {
      toast({ title: "Name required", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const created = await createProperty.mutateAsync({
        name,
        address: manualAddress.trim() || undefined,
      });
      if (manualUnit.trim()) {
        await updateProperty.mutateAsync({
          propertyId: created.id,
          unit: manualUnit.trim(),
        });
      }
      onClose();
      navigate(`/properties/${created.id}`);
    } catch (err) {
      toast({
        title: "Could not save",
        description: err instanceof Error ? err.message : "Failed",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const openCreated = () => {
    onClose();
    if (createdId) navigate(`/properties/${createdId}`);
  };

  const primaryBtn =
    "h-[54px] rounded-[14px] bg-[#24a89a] px-[30px] text-[17px] font-bold text-white shadow-none hover:bg-[#1f958a] disabled:bg-[#cfdcdf] disabled:text-white";
  const ghostLink = "text-[14px] font-medium text-[#64748b] hover:text-[#24a89a]";

  return (
    <div className="flex max-h-[min(85vh,720px)] flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b-[1.5px] border-[#e6edf1] px-[22px] py-[18px]">
        <div className="flex min-w-0 items-center gap-2.5">
          {canBack ? (
            <button
              type="button"
              onClick={goBack}
              className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[11px] border-[1.5px] border-[#e6edf1] hover:bg-[#f4f7f9]"
              aria-label="Back"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 5l-7 7 7 7" />
              </svg>
            </button>
          ) : null}
          <div className="truncate text-[20px] font-bold tracking-[-0.3px] text-[#0f1720]">{sheetTitle[step]}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[11px] border-[1.5px] border-[#e6edf1] hover:bg-[#f4f7f9]"
          aria-label="Close"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.4" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {step === "choice" ? (
          <div className="flex flex-col gap-3 px-[22px] py-5 pb-6">
            <button
              type="button"
              onClick={() => void goHospitable()}
              className="flex items-center gap-3.5 rounded-2xl border-[1.5px] border-[#e6edf1] px-[18px] py-[18px] text-left hover:border-[#24a89a] hover:bg-[#f7fbfb]"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px] bg-[#e2eeed] text-[17px] font-bold text-[#15776d]">
                H
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[18px] font-bold text-[#0f1720]">Hospitable</div>
                <div className="text-[14px] font-medium text-[#64748b]">Import with API key</div>
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 5l7 7-7 7" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setStep("manual")}
              className="flex items-center gap-3.5 rounded-2xl border-[1.5px] border-[#e6edf1] px-[18px] py-[18px] text-left hover:border-[#24a89a] hover:bg-[#f7fbfb]"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px] bg-[#eef3f6] text-[#475569]">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 20h4l10-10-4-4L4 16z" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[18px] font-bold text-[#0f1720]">Manual</div>
                <div className="text-[14px] font-medium text-[#64748b]">Name and address</div>
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        ) : null}

        {step === "connect" ? (
          <>
            <div className="flex flex-col gap-2.5 px-[22px] py-[22px]">
              <Label className="text-[12px] font-bold tracking-[0.3px] text-[#64748b]">API key</Label>
              <Input
                type="password"
                autoComplete="off"
                value={patInput}
                onChange={(e) => setPatInput(e.target.value)}
                placeholder="hos_pat_…"
                className="h-[54px] rounded-[13px] border-[1.5px] border-[#24a89a] font-mono text-[16px] font-medium shadow-none focus-visible:ring-[#24a89a]"
              />
            </div>
            <div className="flex items-center gap-3.5 border-t-[1.5px] border-[#e6edf1] px-[22px] py-4">
              <Button
                type="button"
                disabled={busy || patInput.trim().length < 20}
                onClick={() => void savePat()}
                className={primaryBtn}
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Connect"}
              </Button>
              <button type="button" className={ghostLink} onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        ) : null}

        {step === "units" ? (
          <>
            <div className="flex flex-col gap-3 px-[22px] pb-1.5 pt-5">
              <div className="text-[12px] font-bold tracking-[0.3px] text-[#64748b]">Units</div>
              {loadingList ? (
                <div className="flex items-center gap-2 py-8 text-[14px] text-[#64748b]">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : units.length === 0 ? (
                <p className="py-8 text-[14px] font-medium text-[#64748b]">No units</p>
              ) : (
                <div className="flex max-h-[340px] flex-col gap-2.5 overflow-y-auto">
                  {units.map((u) => {
                    const taken = linkedHospitableIds.has(u.id);
                    const selected = selectedId === u.id;
                    return (
                      <button
                        key={u.id}
                        type="button"
                        disabled={taken}
                        onClick={() => !taken && setSelectedId(u.id)}
                        className={cn(
                          "flex items-center gap-3.5 rounded-[14px] border-[1.5px] px-4 py-3.5 text-left",
                          selected ? "border-[#24a89a] bg-[#eaf6f4]" : "border-[#e6edf1] bg-white",
                          taken && "cursor-not-allowed opacity-45",
                        )}
                      >
                        <div
                          className={cn(
                            "flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px] bg-white",
                            selected ? "border-[#24a89a]" : "border-[#d7e0e6]",
                          )}
                        >
                          {selected ? <div className="h-2.5 w-2.5 rounded-full bg-[#24a89a]" /> : null}
                        </div>
                        <div className="h-12 w-12 shrink-0 overflow-hidden rounded-[10px] bg-[#eef3f6]">
                          {u.picture_url ? (
                            <img
                              src={u.picture_url}
                              alt=""
                              className="h-full w-full object-cover"
                              loading="lazy"
                              referrerPolicy="no-referrer"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-[#94a3b8]">
                              <Building2 className="h-5 w-5" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[16px] font-bold text-[#0f1720]">{u.name}</div>
                          <div className="truncate text-[13px] font-medium text-[#64748b]">
                            {taken ? "Already in hub" : u.address || "—"}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="mt-3.5 flex items-center gap-3.5 border-t-[1.5px] border-[#e6edf1] px-[22px] py-4">
              <Button
                type="button"
                disabled={!selectedId || previewLoading}
                onClick={() => void goPreview()}
                className={primaryBtn}
              >
                Next
              </Button>
              <button type="button" className={ghostLink} onClick={goBack}>
                Back
              </button>
            </div>
          </>
        ) : null}

        {step === "preview" ? (
          previewLoading || !preview ? (
            <div className="flex items-center justify-center gap-2 px-6 py-16 text-[14px] text-[#64748b]">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              <div className="flex gap-0.5 bg-[#e6edf1]">
                {(preview.image_urls.length ? preview.image_urls : [null]).slice(0, 4).map((url, i) => (
                  <div
                    key={i}
                    className="h-[132px] min-w-0 flex-1 bg-[#dde4ea]"
                    style={
                      url
                        ? { backgroundImage: `url(${url})`, backgroundSize: "cover", backgroundPosition: "center" }
                        : undefined
                    }
                  />
                ))}
              </div>
              <div className="flex flex-col gap-4 px-[22px] pb-1.5 pt-[18px]">
                <div className="flex flex-col gap-0.5">
                  <div className="text-[22px] font-bold tracking-[-0.4px] text-[#0f1720]">{preview.name}</div>
                  <div className="text-[15px] font-medium text-[#64748b]">
                    {preview.formatted_address || preview.address || "—"}
                  </div>
                </div>
                <div className="flex gap-2.5">
                  {[
                    { n: dash(preview.beds ?? preview.bedrooms), l: "BEDS" },
                    { n: dash(preview.baths), l: "BATHS" },
                    { n: dash(preview.rooms_count ?? (preview.room_labels.length || null)), l: "ROOMS" },
                  ].map((s) => (
                    <div
                      key={s.l}
                      className="flex flex-1 flex-col gap-0.5 rounded-[14px] border-[1.5px] border-[#e6edf1] bg-[#f4f7f9] px-3.5 py-3"
                    >
                      <div className="text-[22px] font-bold text-[#0f1720]">{s.n}</div>
                      <div className="text-[12px] font-bold tracking-[0.5px] text-[#64748b]">{s.l}</div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col">
                  {(
                    [
                      ["Check-in", preview.check_in],
                      ["Check-out", preview.check_out],
                      ["Summary", preview.summary],
                      ["Hospitable ID", shortId(preview.id)],
                      ["iCal", preview.ical_url ? "Connected" : null],
                    ] as const
                  ).map(([label, value]) => {
                    const missing = !value;
                    return (
                      <div
                        key={label}
                        className="flex items-center justify-between gap-4 border-b-[1.5px] border-[#f1f5f8] py-[11px] last:border-0"
                      >
                        <div className="text-[14px] font-medium text-[#64748b]">{label}</div>
                        <div
                          className={cn(
                            "max-w-[60%] text-right text-[14px]",
                            missing ? "font-medium text-[#b6c2cc]" : "font-bold text-[#0f1720]",
                            label === "Hospitable ID" && "font-mono",
                          )}
                        >
                          {missing ? "—" : value}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: "Photos", on: preview.image_urls.length > 0 },
                    { label: "Rooms", on: (preview.room_labels?.length || 0) > 0 },
                    { label: "Calendar", on: Boolean(preview.ical_url) },
                    { label: "Linked", on: true },
                  ].map((c) => (
                    <div
                      key={c.label}
                      className={cn(
                        "rounded-full px-[11px] py-1.5 text-[12px] font-bold tracking-[0.4px]",
                        c.on ? "bg-[#d7efec] text-[#15776d]" : "bg-[#eef3f6] text-[#64748b]",
                      )}
                    >
                      {c.label}
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-3.5 flex items-center gap-3.5 border-t-[1.5px] border-[#e6edf1] px-[22px] py-4">
                <Button type="button" disabled={busy} onClick={() => void runImport()} className={primaryBtn}>
                  Import
                </Button>
                <button type="button" className={ghostLink} onClick={goBack}>
                  Back
                </button>
              </div>
            </>
          )
        ) : null}

        {step === "importing" ? (
          <div className="flex flex-col items-center gap-5 px-[22px] py-[34px] pb-[38px]">
            <div className="text-[20px] font-bold text-[#0f1720]">{preview?.name || "Importing"}</div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-[#e6edf1]">
              <div
                className="h-full rounded-full bg-[#24a89a] transition-all duration-300"
                style={{ width: `${Math.min(100, progress)}%` }}
              />
            </div>
            <div className="text-[14px] font-medium text-[#64748b]">{importStatus}</div>
          </div>
        ) : null}

        {step === "done" ? (
          <>
            <div className="flex flex-col items-center gap-4 px-[22px] pb-3 pt-[30px]">
              <div className="flex h-[62px] w-[62px] items-center justify-center rounded-full bg-[#d7efec]">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#15776d" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 13l4.5 4.5L19 7" />
                </svg>
              </div>
              <div className="text-center text-[22px] font-bold tracking-[-0.3px] text-[#0f1720]">
                {preview?.name}
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                {doneChips.map((label) => (
                  <div
                    key={label}
                    className="rounded-full bg-[#d7efec] px-[11px] py-1.5 text-[12px] font-bold tracking-[0.4px] text-[#15776d]"
                  >
                    {label}
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-3 border-t-[1.5px] border-[#e6edf1] px-[22px] py-[18px]">
              <Button type="button" onClick={openCreated} className={cn(primaryBtn, "w-full")}>
                Open
              </Button>
            </div>
          </>
        ) : null}

        {step === "manual" ? (
          <>
            <div className="flex flex-col gap-4 px-[22px] pb-1.5 pt-5">
              <div className="flex flex-col gap-2">
                <Label className="text-[12px] font-bold tracking-[0.3px] text-[#64748b]">Name</Label>
                <Input
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  placeholder="Queen E Suite"
                  className="h-[52px] rounded-[13px] border-[1.5px] border-[#24a89a] text-[16px] font-medium shadow-none focus-visible:ring-[#24a89a]"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-[12px] font-bold tracking-[0.3px] text-[#64748b]">Address</Label>
                <Input
                  value={manualAddress}
                  onChange={(e) => setManualAddress(e.target.value)}
                  placeholder="77 Queen St E, Toronto"
                  className="h-[52px] rounded-[13px] border-[1.5px] border-[#e6edf1] text-[16px] font-medium shadow-none focus-visible:ring-[#24a89a]"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-[12px] font-bold tracking-[0.3px] text-[#64748b]">Unit · optional</Label>
                <Input
                  value={manualUnit}
                  onChange={(e) => setManualUnit(e.target.value)}
                  placeholder="—"
                  className="h-[52px] rounded-[13px] border-[1.5px] border-[#e6edf1] text-[16px] font-medium shadow-none placeholder:text-[#cbd5dd] focus-visible:ring-[#24a89a]"
                />
              </div>
            </div>
            <div className="mt-3.5 flex items-center gap-3.5 border-t-[1.5px] border-[#e6edf1] px-[22px] py-4">
              <Button
                type="button"
                disabled={busy || !manualName.trim()}
                onClick={() => void saveManual()}
                className={primaryBtn}
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
              <button type="button" className={ghostLink} onClick={goBack}>
                Back
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
